import React from 'react'
import * as Styled from './feature-error.styled'
import { useTranslation, withTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import Img400 from './images/400.svg'
import Img403 from './images/403.svg'
import Img404 from './images/404.svg'
import Img500 from './images/500.svg'
import Img502 from './images/502.svg'
import Img503 from './images/503.svg'
import Img504 from './images/504.svg'
import { type ErrorInterface } from './types/error.interface'
import { Button } from 'ui/button/ui-button'

interface FeatureErrorProps {
  error: string
}

const FeatureErrorComponent: React.FC<FeatureErrorProps> = ({ error }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const errors: Record<string, ErrorInterface> = {
    400: {
      title: t('Йой!'),
      text: [
        t('Не хвилюйтеся, це лише помилка 400'),
        t('Сервер не зміг обробити Ваш запит')
      ],
      image: Img400,
      hint: t('Поверніться і спробуйте ще раз'),
      buttons: {
        back: true,
        home: true,
        tryAgain: false
      }
    },
    403: {
      title: t('Ой, лишенько!'),
      text: [
        t('Не хвилюйтеся, це лише помилка 403'),
        t('У Вас немає доступу до цієї сторінки')
      ],
      image: Img403,
      hint: t('Увійдіть у систему під відповідною роллю'),
      buttons: {
        back: true,
        home: true,
        tryAgain: false
      }
    },
    404: {
      title: t('Ой, леле!'),
      text: [
        t('Не хвилюйтеся, це лише помилка 404'),
        t('Сторінки, яку ви шукаєте, немає')
      ],
      image: Img404,
      hint: t('Перевірте адресу сторінки, або поверніться на головну'),
      buttons: {
        back: true,
        home: true,
        tryAgain: false
      }
    },
    500: {
      title: t('Ууух!'),
      text: [
        t('Не хвилюйтеся, це лише помилка 500'),
        t('Сервер має труднощі з роботою')
      ],
      image: Img500,
      hint: t('Ми намагаємося виправити проблеми. Спробуйте виконати дію через якийсь час'),
      buttons: {
        back: false,
        home: true,
        tryAgain: true
      }
    },
    502: {
      title: t('Ооох!'),
      text: [
        t('Не хвилюйтеся, це лише помилка 502'),
        t('Зросло навантаження на сервері')
      ],
      image: Img502,
      hint: t('Ми намагаємося виправити проблеми. Спробуйте виконати дію через якийсь час'),
      buttons: {
        back: false,
        home: true,
        tryAgain: true
      }
    },
    503: {
      title: t('Ааай!'),
      text: [
        t('Не хвилюйтеся, це лише помилка 503'),
        t('Зросло навантаження на сервері')
      ],
      image: Img503,
      hint: t('Ми намагаємося виправити проблеми. Спробуйте виконати дію через якийсь час'),
      buttons: {
        back: false,
        home: true,
        tryAgain: true
      }
    },
    504: {
      title: t('Ееех!'),
      text: [
        t('Не хвилюйтеся, це лише помилка 504'),
        t('Зросло навантаження на сервері')
      ],
      image: Img504,
      hint: t('Ми намагаємося виправити проблеми. Спробуйте виконати дію через якийсь час'),
      buttons: {
        back: false,
        home: true,
        tryAgain: true
      }
    }
  }

  const currentError = errors[error] || errors['404']

  return (
    <Styled.Wrap>
      <Styled.Title>{currentError.title}</Styled.Title>
      <div>
        {currentError.text.map(text => <Styled.Text key={text}>{text}</Styled.Text>)}
      </div>
      <Styled.Img src={currentError.image} alt="Error" />
      <Styled.Hint>{currentError.hint}</Styled.Hint>
      <Styled.ButtonWrap>
        <Button format='transparent-grey'
          onClick={currentError.buttons.tryAgain ? () => { navigate(0) } : () => { navigate(-1) }}
        >
          { currentError.buttons.tryAgain ? t('Спробувати ще раз') : t('Назад') }
        </Button>
        <Button format='blue' to="/">{t('На головну')}</Button>
      </Styled.ButtonWrap>
      <Styled.Support>
        {t('Потрібна допомога? Звертайтеся до нашої техпідтримки:')} <a href="mailto:support@dzo.com.ua" rel="noopener noreferrer nofollow">support@dzo.com.ua</a>.
      </Styled.Support>
    </Styled.Wrap>
  )
}

const FeatureError = withTranslation('dzo')(FeatureErrorComponent)

export default FeatureError
